// Cartes KPI pour le dashboard

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  useTheme,
} from '@mui/material';
import {
  Euro,
  Description,
  CheckCircle,
  Warning,
  AccountBalanceWallet,
} from '@mui/icons-material';
import { DashboardKPIs } from '../../types/dashboard';

interface KPICardProps {
  titre: string;
  valeur: number;
  icon: React.ReactNode;
  couleur: string;
  formatValeur?: (valeur: number) => string;
  subtitle?: string;
}

const KPICard: React.FC<KPICardProps> = ({
  titre,
  valeur,
  icon,
  couleur,
  formatValeur = (v) => v.toLocaleString('fr-FR'),
  subtitle,
}) => {
  const theme = useTheme();

  return (
    <Card
      elevation={2}
      sx={{
        height: '100%',
        borderLeft: `4px solid ${couleur}`,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[6],
        },
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            {titre}
          </Typography>
          <Box
            sx={{
              backgroundColor: `${couleur}20`,
              borderRadius: 1,
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {formatValeur(valeur)}
        </Typography>

        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

interface KPICardsProps {
  kpis: DashboardKPIs;
}

export const KPICards: React.FC<KPICardsProps> = ({ kpis }) => {
  const theme = useTheme();

  return (
    <Grid container spacing={2}>
      {/* CA Encaissé */}
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          titre="CA Encaissé"
          valeur={kpis.caEncaisse}
          icon={<AccountBalanceWallet sx={{ color: theme.palette.success.main }} />}
          couleur={theme.palette.success.main}
          formatValeur={(v) => `${v.toLocaleString('fr-FR')} €`}
          subtitle="Montant réellement perçu"
        />
      </Grid>

      {/* CA Facturé */}
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          titre="CA Facturé"
          valeur={kpis.caFacture}
          icon={<Euro sx={{ color: theme.palette.primary.main }} />}
          couleur={theme.palette.primary.main}
          formatValeur={(v) => `${v.toLocaleString('fr-FR')} €`}
          subtitle="Montant total facturé"
        />
      </Grid>

      {/* Devis en attente */}
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          titre="Devis en attente"
          valeur={kpis.nbDevisEnAttente}
          icon={<Description sx={{ color: theme.palette.info.main }} />}
          couleur={theme.palette.info.main}
          subtitle={`Taux conversion: ${kpis.tauxConversion.toFixed(1)}%`}
        />
      </Grid>

      {/* Montant en cours (impayés) */}
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          titre="Montant en cours"
          valeur={kpis.montantEncours}
          icon={<Warning sx={{ color: theme.palette.error.main }} />}
          couleur={theme.palette.error.main}
          formatValeur={(v) => `${v.toLocaleString('fr-FR')} €`}
          subtitle="Factures impayées"
        />
      </Grid>
    </Grid>
  );
};
